import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { IoCloudUploadOutline, IoSaveOutline, IoKeyOutline, IoDocumentLockOutline, IoBusinessOutline, IoCheckmarkCircle, IoWalletOutline } from 'react-icons/io5';
import './ConfiguracionFiscalScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

function ConfiguracionFiscalScreen() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        rfc: '',
        razonSocial: '',
        regimenFiscal: '',
        cp: '',
        passwordKey: ''
    });

    const [files, setFiles] = useState({
        cer: null,
        key: null
    });

    // SAT Catalog for Regimen Fiscal (Simplified)
    const regimenes = [
        { id: '601', label: '601 - General de Ley Personas Morales' },
        { id: '603', label: '603 - Personas Morales con Fines no Lucrativos' },
        { id: '605', label: '605 - Sueldos y Salarios' },
        { id: '606', label: '606 - Arrendamiento' },
        { id: '612', label: '612 - Personas Físicas con Actividades Empresariales' },
        { id: '621', label: '621 - Incorporación Fiscal' },
        { id: '626', label: '626 - Régimen Simplificado de Confianza (RESICO)' },
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'rfc' ? value.toUpperCase() : value
        }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            // Basic validation
            const ext = file.name.split('.').pop().toLowerCase();
            if (ext !== type) {
                alert(`Por favor sube un archivo con extensión .${type}`);
                return;
            }
            setFiles(prev => ({ ...prev, [type]: file }));
        }
    };

    const validateRFC = (rfc) => {
        const regex = /^([A-ZÑ&]{3,4}) ?(?:- ?)?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])) ?(?:- ?)?([A-Z\d]{2})([A\d])$/;
        return regex.test(rfc);
    };

    const { userData } = useAuth();
    const [isConfigured, setIsConfigured] = useState(false);

    useEffect(() => {
        const fetchConfig = async () => {
            if (!userData?.condominioId) return;

            try {
                setLoading(true);
                const docRef = doc(db, 'condominios', userData.condominioId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data.datosFiscales) {
                        setFormData({
                            rfc: data.datosFiscales.rfc || '',
                            razonSocial: data.datosFiscales.razonSocial || '',
                            regimenFiscal: data.datosFiscales.regimen || '',
                            cp: data.datosFiscales.cp || '',
                            passwordKey: ''
                        });
                        setIsConfigured(!!data.facturapiOrganizationId);
                    }
                }
            } catch (error) {
                console.error("Error fetching condo config:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchConfig();
    }, [userData?.condominioId]);

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!validateRFC(formData.rfc)) {
            alert("El RFC ingresado no tiene un formato válido.");
            return;
        }

        if (!isConfigured) {
            if (!files.cer || !files.key || !formData.passwordKey) {
                alert("Para la configuración inicial, debes subir tus archivos CSD (.cer y .key) y la contraseña.");
                return;
            }
        } else {
            const isPartialUpload = (files.cer || files.key || formData.passwordKey) &&
                (!files.cer || !files.key || !formData.passwordKey);

            if (isPartialUpload) {
                alert("Si vas a actualizar tus sellos, debes subir ambos archivos (.cer y .key) y la contraseña.");
                return;
            }
        }

        setLoading(true);

        try {
            let cerBase64 = null;
            let keyBase64 = null;

            if (files.cer) {
                cerBase64 = await fileToBase64(files.cer);
            }
            if (files.key) {
                keyBase64 = await fileToBase64(files.key);
            }

            const uploadFiscalFn = httpsCallable(functions, 'uploadFiscalConfig');
            const { data: result } = await uploadFiscalFn({
                condominioId: userData?.condominioId || 'GLOBAL',
                rfc: formData.rfc,
                razonSocial: formData.razonSocial,
                regime: formData.regimenFiscal,
                cp: formData.cp,
                cerBase64,
                keyBase64,
                password: formData.passwordKey
            });
            if (result?.error) {
                throw new Error(result.error || "Error en el servidor");
            }

            alert("¡Éxito! Configuración Fiscal validada y guardada en Facturapi.");
            navigate('/admin/finanzas');

        } catch (error) {
            console.error("Error saving fiscal config:", error);
            alert("Error: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/admin/configuracion-fiscal" />

            <main className="ks-main">
                <KondoBrandHeader title="Administración" showBackButton={false} />

                <div className="ks-scroll-area">
                    <div className="fiscal-container">
                        {/* --- Premium Banner --- */}
                        <div className="muro-header-stitch">
                            <div className="muro-hero-card">
                                <div className="muro-hero-content">
                                    <h1>Configuración Fiscal</h1>
                                    <p>Gestiona la facturación automática (SAT) y la pasarela de pagos para el cobro de cuotas.</p>
                                </div>
                                <IoWalletOutline className="muro-hero-icon-decor" />
                            </div>
                        </div>

                        <div className="fiscal-body">
                            {/* STRIPE CARD */}
                            <div className="stripe-connect-card">
                                <div className="stripe-info">
                                    <div className="stripe-icon-wrapper">
                                        <IoWalletOutline size={26} color="#7B61FF" />
                                    </div>
                                    <div className="stripe-text">
                                        <h3>Pasarela de Pagos (Stripe)</h3>
                                        <p>Vincula tu cuenta bancaria para recibir pagos de cuotas con tarjeta y transferencias automatizadas.</p>
                                    </div>
                                </div>
                                <button className="action-btn-stripe" onClick={() => navigate('/configuracion-pagos')}>
                                    Configurar Pagos
                                </button>
                            </div>

                            {isConfigured && (
                                <div className="fiscal-status-active">
                                    <IoCheckmarkCircle size={32} color="#16a34a" />
                                    <div>
                                        <strong>¡Configuración Fiscal Activa!</strong>
                                        <span>Tu condominio está listo para timbrar facturas 4.0 automáticamente. Los certificados (CSD) están cargados y vigentes.</span>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSave}>
                                <div className="fiscal-card">
                                    <div className="section-title">
                                        <IoBusinessOutline size={22} color="#7B61FF" />
                                        Datos de Facturación
                                    </div>

                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label className="form-label">RFC</label>
                                            <input
                                                name="rfc"
                                                value={formData.rfc}
                                                onChange={handleInputChange}
                                                className="form-input"
                                                placeholder="XAXX010101000"
                                                maxLength={13}
                                                required
                                            />
                                            <div className="form-hint">Registro Federal de Contribuyentes</div>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Razón Social</label>
                                            <input
                                                name="razonSocial"
                                                value={formData.razonSocial}
                                                onChange={handleInputChange}
                                                className="form-input"
                                                placeholder="Ej. Condominio Las Palmas AC"
                                                required
                                            />
                                            <div className="form-hint">Tal cual aparece en la Constancia de Situación Fiscal</div>
                                        </div>

                                        <div className="form-group full-width">
                                            <label className="form-label">Régimen Fiscal</label>
                                            <select
                                                name="regimenFiscal"
                                                value={formData.regimenFiscal}
                                                onChange={handleInputChange}
                                                className="form-select"
                                                required
                                            >
                                                <option value="">Seleccione una opción...</option>
                                                {regimenes.map(reg => (
                                                    <option key={reg.id} value={reg.id}>{reg.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Código Postal</label>
                                            <input
                                                name="cp"
                                                value={formData.cp}
                                                onChange={handleInputChange}
                                                className="form-input"
                                                placeholder="12345"
                                                maxLength={5}
                                                type="number"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="csd-upload-section">
                                        <div className="section-title">
                                            <IoDocumentLockOutline size={22} color="#7B61FF" />
                                            Certificados de Sello Digital (CSD)
                                        </div>
                                        <p className="section-hint">
                                            Necesitamos los archivos que te entregó el SAT (.cer y .key) específicamente de tu firma de SELLOS, no la FIEL.
                                        </p>

                                        <div className="files-grid">
                                            <label className={`file-dropzone ${files.cer ? 'has-file' : ''}`}>
                                                <input
                                                    type="file"
                                                    accept=".cer"
                                                    onChange={(e) => handleFileChange(e, 'cer')}
                                                    style={{ display: 'none' }}
                                                />
                                                <div className="dropzone-content">
                                                    <IoCloudUploadOutline className="upload-icon" />
                                                    {files.cer ? (
                                                        <span className="file-name">{files.cer.name}</span>
                                                    ) : (
                                                        <>
                                                            <strong>Certificado (.cer)</strong>
                                                            <span>Clic para seleccionar</span>
                                                        </>
                                                    )}
                                                </div>
                                            </label>

                                            <label className={`file-dropzone ${files.key ? 'has-file' : ''}`}>
                                                <input
                                                    type="file"
                                                    accept=".key"
                                                    onChange={(e) => handleFileChange(e, 'key')}
                                                    style={{ display: 'none' }}
                                                />
                                                <div className="dropzone-content">
                                                    <IoKeyOutline className="upload-icon" />
                                                    {files.key ? (
                                                        <span className="file-name">{files.key.name}</span>
                                                    ) : (
                                                        <>
                                                            <strong>Llave Privada (.key)</strong>
                                                            <span>Clic para seleccionar</span>
                                                        </>
                                                    )}
                                                </div>
                                            </label>
                                        </div>

                                        <div className="form-group full-width" style={{ marginTop: '24px' }}>
                                            <label className="form-label">Contraseña de la Llave Privada</label>
                                            <input
                                                type="password"
                                                name="passwordKey"
                                                value={formData.passwordKey}
                                                onChange={handleInputChange}
                                                className="form-input"
                                                placeholder="••••••••••••"
                                                required={!isConfigured}
                                                autoComplete="new-password"
                                            />
                                            <div className="form-hint">La contraseña asociada a estos archivos CSD.</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button type="button" onClick={() => navigate(-1)} className="btn-cancel" disabled={loading}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn-save" disabled={loading}>
                                        {loading ? 'Validando...' : (
                                            <>
                                                <IoSaveOutline size={20} />
                                                Guardar Configuración
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </main>

            <KondoBottomNav activeRoute="/admin/configuracion-fiscal" />
        </div>
    );
}

export default ConfiguracionFiscalScreen;
